//
// [アドイン] 全員の持ち出し履歴の表示とエクセル出力（サーバー対応）
//
// ./CheckoutHistoryExportModal.js
//


import React, { useEffect, useState } from 'react';
import CheckoutHistoryEditModal from './CheckoutHistoryEditModal';
import * as XLSX from 'xlsx';
import { useEscapeClose } from '../hooks/useEscapeClose';
import DoubleConfirmModal from './doubleConfirmModal';

function CheckoutHistoryExportModal({ onClose, ...props }) {
  useEscapeClose(onClose); // ← Escキーでモーダルを閉じる

  const [userList, setUserList] = useState([]);
  const [modalConfig, setModalConfig] = useState(null);

  useEffect(() => {
    fetch("https://newells4610.stars.ne.jp/data/userList.json", { cache: "no-store" })
      .then(res => res.json())
      .then(data => setUserList(data))
      .catch(err => {
        console.error("ユーザーリスト取得エラー", err);
        setUserList([]);
      });
  }, []);

  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewerTarget, setViewerTarget] = useState(null);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    const registeredUsers = userList
      .filter(u => u.name && u.name.trim() !== '' && u.name !== '未設定')
      .map(u => ({ id: u.id, name: u.name }));
    setUsers(registeredUsers);
  }, [userList]);

  const handleCheckboxChange = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(users.map(u => u.id));
    }
    setSelectAll(!selectAll);
  };


// 履歴のエクセル出力 --------------------------------------------------------------------
  const handleExport = async () => {
    try {
      const res = await fetch("https://newells4610.stars.ne.jp/data/checkoutrecords.json", {
        cache: "no-store"
      });
      const records = await res.json();
      const filtered = records.filter(r => selectedIds.includes(r.userId || r.user));

      if (filtered.length === 0) {
        setModalConfig({
          message: '出力対象の履歴がありません',
          onConfirm: () => setModalConfig(null),
        });
        return;
      }

      const wb = XLSX.utils.book_new();
      const grouped = {};

      filtered.forEach(record => {
        // ユーザーIDに対応する名前を userList から取得
        const user = userList.find(u => u.id === record.userId || u.id === record.user);
        const userName = user?.name || record.userId || record.user;

        if (!grouped[userName]) grouped[userName] = [];
        record.items.forEach(item => {
          grouped[userName].push({
            日時: new Date(record.timestamp).toLocaleString(),
            品名: item.name,
            品番: item.productId,
            数量: item.quantity,
            単位: item.unit || ''
          });
        });
      });

      Object.entries(grouped).forEach(([name, data]) => {
        const ws = XLSX.utils.json_to_sheet(data);
        
        // セルの幅を調整
        const maxNameWidth = Math.max(...data.map(row => (row['品名'] || '').length), 10);
        const maxIdWidth = Math.max(...data.map(row => (row['品番'] || '').length), 8);
        ws['!cols'] = [
          { wch: 20 },
          { wch: Math.max(maxNameWidth, 20) },
          { wch: Math.max(maxIdWidth, 15) },
          { wch: 6 },
          { wch: 6 }
        ];
        XLSX.utils.book_append_sheet(wb, ws, name);
      });

      const date = new Date().toISOString().slice(0, 10);
      const fileName = selectAll
        ? `持出履歴_一括_${date}.xlsx`
        : `持出履歴_${users.find(u => u.id === selectedIds[0])?.name || '不明'}さん他${selectedIds.length - 1}名_${date}.xlsx`;

      XLSX.writeFile(wb, fileName);
    } catch (err) {
        console.error("履歴出力失敗", err);
        setModalConfig({
          message: '履歴の読み込みに失敗しました',
          onConfirm: () => setModalConfig(null),
        });
      }
    };


// チェックした人の履歴削除 ------------------------------------------------------------
  const handleDelete = () => {
    setModalConfig({
      message: 'チェックした人の履歴を削除しますか？',
      showCancel: true,
      onConfirm: async () => {
        setModalConfig(null);
        try {
          const res = await fetch("https://newells4610.stars.ne.jp/data/checkoutrecords.json", {
            cache: "no-store"
          });
          const records = await res.json();

          const remaining = records.filter(r => !selectedIds.includes(r.userId || r.user));

          const updateRes = await fetch("https://newells4610.stars.ne.jp/api/update_records.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ records: remaining })
          });

          const result = await updateRes.json();
          if (result.status === "ok") {
            setSelectedIds([]);
            setModalConfig({
              message: '削除が完了しました',
              onConfirm: () => setModalConfig(null),
            });
          } else {
            setModalConfig({
              message: 'サーバー側の削除に失敗しました: ' + result.message,
              onConfirm: () => setModalConfig(null),
            });
          }
        } catch (err) {
          console.error("削除失敗", err);
          setModalConfig({
            message: '通信に失敗しました',
            onConfirm: () => setModalConfig(null),
          });
        }
      },
      onCancel: () => setModalConfig(null),
    });
  };


// 個人履歴の表示・個別削除 --------------------------------------------------------
  const handleView = () => {
    if (selectedIds.length !== 1) {
      setModalConfig({
        message: '履歴表示は 1 人を選ばなきゃ駄目だぞ。',
        onConfirm: () => setModalConfig(null),
      });
      return;
    }
    setViewerTarget(selectedIds[0]);
  };


// -----------------------------------------------------------------------
  return (
    <>
      {/* モーダルが開く条件 */}
      {viewerTarget && (
        <CheckoutHistoryEditModal
          userId={viewerTarget}
          onClose={() => setViewerTarget(null)}
        />
      )}

      {/* ↓ これは DoubleConfirmModal 用の overlay なので OK！ */}
      {modalConfig && <DoubleConfirmModal {...modalConfig} />}

      {/* ✅ ここは通常のレイアウト：overlay 使わない */}
      <div className="modal-box" style={{ width: '900px', maxWidth: '70%' }}>
        <h4>全員の持ち出し履歴　［表示・出力］</h4>

        <div className="modal-list small-text" style={{
          maxHeight: '50vh',
          overflowY: 'auto',
          display: 'flex',
          flexWrap: 'wrap'
        }}>
          <label style={{ width: '100%', marginBottom: '1em' }}>
            <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
            一括 選択・解除
          </label>
          {users.map((u, i) => (
            <label key={u.id} style={{ width: '30%', margin: '0.5em 0', wordBreak: 'break-word' }}>
              <input
                type="checkbox"
                checked={selectedIds.includes(u.id)}
                onChange={() => handleCheckboxChange(u.id)}
              />
              <div style={{ display: 'inline-block', marginLeft: '0.5em' }}>
                <span>{u.name}</span><br />
                <small style={{ fontSize: '1em', color: '#555' }}>{u.id}</small>
              </div>
            </label>
          ))}
        </div>

        <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1em' }}>
          <div>
            <button onClick={handleView}>個人履歴の表示・部分削除</button>
            <button onClick={handleExport} style={{ marginLeft: '3em' }}>履歴Excel出力</button>
          </div>
          <button onClick={handleDelete} style={{ background: '#faa' }}>チェックした人の全履歴削除</button>
          <button onClick={onClose}>閉じる</button>
        </div>
      </div>
    </>
  );
}

export default CheckoutHistoryExportModal;
